export const pct = (v?: number) =>
  v == null || !isFinite(v) ? "—" : `${(v * 100).toFixed(1)}%`;
export const num = (v?: number, d = 2) =>
  v == null || !isFinite(v) ? "—" : v.toFixed(d);
export const money = (v?: number) =>
  v == null || !isFinite(v) ? "—" : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
export const money2 = (v?: number) =>
  v == null || !isFinite(v) ? "—" : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const price = (v?: number) =>
  v == null || !isFinite(v) ? "—" : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const p2 = (n: number) => String(n).padStart(2, "0");

/** Exact UTC timestamp — matches the market bar times the engine trades on. */
export const utcTime = (t?: string) => {
  if (!t) return "—";
  const d = new Date(t);
  if (isNaN(d.getTime())) return t;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())} UTC`;
};

/** Compact UTC (no year) for dense tables. */
export const utcShort = (t?: string) => {
  if (!t) return "—";
  const d = new Date(t);
  if (isNaN(d.getTime())) return t;
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`;
};

/** Back-compat alias used by older components — UTC, compact. */
export const shortTime = utcShort;

/** "2 min ago" style, relative to now. */
export const ago = (t?: string) => {
  if (!t) return "";
  const ms = Date.now() - new Date(t).getTime();
  if (!isFinite(ms)) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export const sideLabel = (side?: string | number) => {
  if (side === "long" || side === 1 || side === "1") return "Long";
  if (side === "short" || side === -1 || side === "-1") return "Short";
  return side == null ? "—" : String(side);
};

/** Plain-English meaning of an exit reason, for tooltips / the legend. */
export function exitReason(reason?: string): { label: string; cls: string; explain: string } {
  switch ((reason || "").toLowerCase()) {
    case "tp":
      return { label: "Take-profit", cls: "exit tp", explain: "Price reached the take-profit target — full planned win (≈ +RR×risk)." };
    case "sl":
      return { label: "Stop-loss", cls: "exit sl", explain: "Price hit the stop-loss — full planned loss (−1R)." };
    case "time":
      return { label: "Time exit", cls: "exit time", explain: "Position reached its max-hold limit (8 bars = 32h on 4h) and was closed at the market price — a partial win or loss, not the full target." };
    case "trail":
      return { label: "Trailing stop", cls: "exit trail", explain: "A trailing stop locked in profit and then was hit." };
    case "eod":
      return { label: "Session close", cls: "exit time", explain: "Closed at session end." };
    default:
      return { label: reason || "—", cls: "exit", explain: reason || "" };
  }
}

/** Accessible status -> {label, className, sr description} */
export function statusInfo(status: string) {
  switch (status) {
    case "live":
      return { label: "Live · paper", cls: "badge live", sr: "Live paper-trading — no real capital" };
    case "testing":
      return { label: "Candidate · testing", cls: "badge testing", sr: "Candidate under review, not yet paper-trading" };
    case "paused":
      return { label: "Paused", cls: "badge paused", sr: "Paused" };
    case "retired":
      return { label: "Retired", cls: "badge retired", sr: "Retired" };
    default:
      return { label: status, cls: "badge", sr: status };
  }
}
